import { FC } from 'react';
import { FiX } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';
import { EMPLOYEE_DETAILS_COLUMN_WIDTH, EMPLOYEE_NAME_COLUMN_WIDTH } from '../../helpers/constants';
import { cn } from '../../lib/utils';
import { useContract } from '../../services/contract/useContract';
import { useGetContractFromRole } from '../../services/contract/useGetContractFromRole';
import { DailyEditingType, WorkingShift } from '../../types/daily.types';
import { EmployeeForShiftItem } from '../../types/employeeForShift.types';
import { ShiftContractRoleOption } from '../../types/template.types';
import { AttendanceTotalColumns } from './daily/DailyTotalColumns';
import { useEmployeesForShift } from './hooks/useEmployeesForShift';
import { MonthlyTotalColumns } from './monthly/MonthlyTotalColumns';
import { useWorkingRoster } from './providers/WorkingRosterService';
import { ShiftTimePicker } from './ShiftTimePicker';
import { RosterPicker } from './staffPicker/RosterPicker';

interface Props {
  shift: WorkingShift;
  readOnly?: boolean;
  updateShift: (newShift: WorkingShift) => void;
  removeShift: () => void;
  selectContractRole: (newRole?: ShiftContractRoleOption) => void;
}

export const RosterShift: FC<Props> = ({ shift, readOnly, updateShift, removeShift }) => {
  const getContract = useGetContractFromRole();

  const contractId = shift.assignedContractId
    ? shift.assignedContractId
    : getContract(shift.assignedContractRoleId ?? '')?.id;

  const contract = useContract(contractId);

  const employeeDropdownOptions = useEmployeesForShift(shift);
  const employee = employeeDropdownOptions.find(e => e.id === shift.assignedEmployeeId);

  const { workingRoster } = useWorkingRoster();

  const onSelectStaff = (newStaff?: EmployeeForShiftItem) => {
    const newShift = { ...shift };
    newShift.assignedEmployeeId = newStaff?.id;

    updateShift(newShift);
  };

  let otherShiftsDuration: number | undefined;
  if (employee) {
    const otherShiftsForEmployee =
      workingRoster?.shifts.filter(s => s.assignedEmployeeId === employee.id && s.id !== shift.id) || [];

    otherShiftsDuration = otherShiftsForEmployee.reduce((prevTotal, shift) => {
      return prevTotal + (shift.end || 0) - (shift.start || 0);
    }, 0);
  }

  return (
    <div className="flex flex-row border-b">
      <div className={cn('flex sticky left-0 z-[60] bg-white')}>
        <div
          className="flex items-center px-2 font-semibold gap-3 border-r"
          style={{ width: EMPLOYEE_NAME_COLUMN_WIDTH }}
        >
          {!readOnly && (
            <button onClick={removeShift}>
              <FiX className={twMerge('h-6 w-6')} />
            </button>
          )}
          {readOnly ? (
            <div className="flex flex-col w-[90%] gap-1 text-sm">{employee?.name || '-'}</div>
          ) : (
            <RosterPicker
              shiftId={shift.id}
              options={employeeDropdownOptions}
              onChange={onSelectStaff}
              value={employee}
            />
          )}
        </div>
      </div>
      <div style={{ width: EMPLOYEE_DETAILS_COLUMN_WIDTH }} className="flex">
        <div className="flex-1 flex border-r">
          <MonthlyTotalColumns shift={shift} />
        </div>
        <div className="flex-1 flex">
          <AttendanceTotalColumns shift={shift} />
        </div>
      </div>
      <div className="flex-1">
        <ShiftTimePicker
          key={shift.id}
          shift={shift}
          updateShift={updateShift}
          type={DailyEditingType.ROSTER}
          useColorizedBar={true}
          hasSetEmployee={!!shift.assignedEmployeeId}
          readOnly={readOnly}
          contract={contract}
          otherShiftsDuration={otherShiftsDuration}
        />
      </div>
    </div>
  );
};
